import {
  activeFields,
  isDisplayOnly,
  resolveOptions,
  type AnswerMap,
  type AnswerValue,
  type FieldError,
  type FlowContext,
  type FormDefinition,
  type FormFieldDefinition,
  type OptionList,
} from '@foyer/shared';
import qrcode from 'qrcode-generator';
import { useMemo, useState } from 'react';
import { localized, type Strings } from '../i18n/index.js';
import { Icon } from './ui.js';

/**
 * Renders a tenant-configured form.
 *
 * The kiosk validates with the exact same function the API uses
 * (`validateSubmission` from @foyer/shared), so inline feedback and server
 * rejection can never disagree. This component only decides how a field type
 * *looks* on a tablet.
 */

interface Props {
  form: FormDefinition;
  lists: Record<string, OptionList>;
  values: AnswerMap;
  /** Reason and appointment answer chosen before the form. */
  flow: FlowContext;
  errors: FieldError[];
  language: string;
  strings: Strings;
  onChange: (name: string, value: AnswerValue) => void;
}

/**
 * The visitor's wording for a field error. The engine's own message is
 * English and meant for logs and the API; on the tablet only the kind of
 * problem matters, in the visitor's language.
 */
function errorFor(errors: FieldError[], name: string, strings: Strings): string | null {
  const error = errors.find((e) => e.field === name);
  if (!error) return null;
  return error.code === 'required' ? strings.fieldRequired : strings.fieldInvalid;
}

function inputMode(type: FormFieldDefinition['type']): 'text' | 'tel' | 'email' | 'numeric' {
  if (type === 'phone') return 'tel';
  if (type === 'email') return 'email';
  if (type === 'number') return 'numeric';
  return 'text';
}

/**
 * QR code drawn as one SVG path. Generated on the tablet rather than fetched,
 * so the hint still works when the lobby WLAN is down.
 */
function QrCode({ url }: { url: string }): JSX.Element {
  const path = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(url);
    qr.make();
    const size = qr.getModuleCount();
    let d = '';
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        if (qr.isDark(row, col)) d += `M${col} ${row}h1v1h-1z`;
      }
    }
    return { d, size };
  }, [url]);

  // Four modules of quiet zone, as the standard asks for.
  return (
    <svg
      className="qr"
      viewBox={`-4 -4 ${path.size + 8} ${path.size + 8}`}
      role="img"
      aria-label="QR-Code"
      shapeRendering="crispEdges"
    >
      <rect x={-4} y={-4} width={path.size + 8} height={path.size + 8} fill="#fff" />
      <path d={path.d} fill="#000" />
    </svg>
  );
}

/**
 * A hint without input: heading, text and - if configured - a QR code. Also
 * shown again on the thank-you screen, so a visitor who was busy typing can
 * still scan the link on the way out.
 */
export function InfoBlock({
  field,
  language,
}: {
  field: FormFieldDefinition;
  language: string;
}): JSX.Element {
  return (
    <div className="field field-wide info-block">
      {field.linkUrl ? <QrCode url={field.linkUrl} /> : null}
      <div className="info-body">
        <div className="info-title">{localized(field.label, field.labelI18n, language)}</div>
        {field.helpText ? (
          <p className="info-text">{localized(field.helpText, field.helpTextI18n, language)}</p>
        ) : null}
      </div>
    </div>
  );
}

/** A searchable picker for long lists (districts, properties, employees). */
function SearchSelect({
  field,
  options,
  value,
  language,
  strings,
  invalid,
  onChange,
}: {
  field: FormFieldDefinition;
  options: Array<{ value: string; label: string; labelI18n?: Record<string, string> }>;
  value: string;
  language: string;
  strings: Strings;
  invalid: boolean;
  onChange: (value: string) => void;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const withLabels = options.map((o) => ({ ...o, text: localized(o.label, o.labelI18n, language) }));
    if (!q) return withLabels.slice(0, 12);
    return withLabels.filter((o) => o.text.toLowerCase().includes(q)).slice(0, 12);
  }, [options, query, language]);

  const selected = options.find((o) => o.value === value);

  return (
    <div>
      <input
        className="control"
        type="search"
        inputMode="search"
        placeholder={strings.search}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={`${field.label} - ${strings.search}`}
        aria-invalid={invalid}
        autoComplete="off"
      />
      <div className="choice-chips" style={{ marginTop: 10 }}>
        {selected && !filtered.some((o) => o.value === selected.value) ? (
          <button type="button" className="chip" aria-pressed="true" onClick={() => onChange('')}>
            {localized(selected.label, selected.labelI18n, language)}
          </button>
        ) : null}
        {filtered.map((option) => (
          <button
            key={option.value}
            type="button"
            className="chip"
            aria-pressed={value === option.value}
            onClick={() => onChange(value === option.value ? '' : option.value)}
          >
            {option.text}
          </button>
        ))}
        {filtered.length === 0 ? <span className="help">{strings.noResults}</span> : null}
      </div>
    </div>
  );
}

function FieldControl({
  field,
  lists,
  value,
  language,
  strings,
  invalid,
  onChange,
}: {
  field: FormFieldDefinition;
  lists: Record<string, OptionList>;
  value: AnswerValue;
  language: string;
  strings: Strings;
  invalid: boolean;
  onChange: (value: AnswerValue) => void;
}): JSX.Element {
  const options = resolveOptions(field, lists);
  const id = `field-${field.name}`;
  const describedBy = field.helpText ? `${id}-help` : undefined;
  const choiceLike =
    field.type === 'radio' ||
    field.type === 'select' ||
    field.type === 'multiselect' ||
    field.type === 'search_select' ||
    field.type === 'list_ref';

  // A referenced list can be missing - a stale cached config, or a list the
  // administrator emptied. Without this the field renders as blank space with a
  // label above it and the visitor has no idea whether it is broken or loading.
  if (choiceLike && options.length === 0) {
    return (
      <p className="help" style={{ margin: 0 }} role="status">
        {strings.noOptions}
      </p>
    );
  }

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          id={id}
          className="control"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={localized(field.placeholder ?? '', field.placeholderI18n, language)}
          maxLength={field.validation?.maxLength ?? 2000}
          aria-invalid={invalid}
          aria-describedby={describedBy}
        />
      );

    case 'checkbox':
      return (
        <label className="checkbox-row" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            aria-invalid={invalid}
          />
          <span>{localized(field.label, field.labelI18n, language)}</span>
        </label>
      );

    case 'radio':
    case 'select':
      return (
        <div
          className="choice-chips"
          role="group"
          aria-label={localized(field.label, field.labelI18n, language)}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="chip"
              aria-pressed={value === option.value}
              onClick={() => onChange(value === option.value ? null : option.value)}
            >
              {localized(option.label, option.labelI18n, language)}
            </button>
          ))}
        </div>
      );

    case 'multiselect': {
      const selected = Array.isArray(value) ? value : [];
      return (
        <div
          className="choice-chips"
          role="group"
          aria-label={localized(field.label, field.labelI18n, language)}
        >
          {options.map((option) => {
            const active = selected.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className="chip"
                aria-pressed={active}
                onClick={() =>
                  onChange(active ? selected.filter((v) => v !== option.value) : [...selected, option.value])
                }
              >
                {localized(option.label, option.labelI18n, language)}
              </button>
            );
          })}
        </div>
      );
    }

    case 'search_select':
    case 'list_ref':
      // Short lists render as chips; long ones get a search box, because
      // scrolling a 60 entry dropdown on a tablet is miserable.
      return options.length > 8 ? (
        <SearchSelect
          field={field}
          options={options}
          value={typeof value === 'string' ? value : ''}
          language={language}
          strings={strings}
          invalid={invalid}
          onChange={(v) => onChange(v === '' ? null : v)}
        />
      ) : (
        <div
          className="choice-chips"
          role="group"
          aria-label={localized(field.label, field.labelI18n, language)}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="chip"
              aria-pressed={value === option.value}
              onClick={() => onChange(value === option.value ? null : option.value)}
            >
              {localized(option.label, option.labelI18n, language)}
            </button>
          ))}
        </div>
      );

    case 'date':
      return (
        <input
          id={id}
          className="control"
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value || null)}
          aria-invalid={invalid}
          aria-describedby={describedBy}
        />
      );

    default:
      return (
        <input
          id={id}
          className="control"
          type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
          inputMode={inputMode(field.type)}
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => onChange(e.target.value)}
          placeholder={localized(field.placeholder ?? '', field.placeholderI18n, language)}
          maxLength={field.validation?.maxLength ?? 200}
          autoComplete="off"
          autoCapitalize={field.type === 'email' ? 'none' : 'words'}
          spellCheck={false}
          aria-invalid={invalid}
          aria-describedby={describedBy}
        />
      );
  }
}

export function FormRenderer({
  form,
  lists,
  values,
  flow,
  errors,
  language,
  strings,
  onChange,
}: Props): JSX.Element {
  const fields = useMemo(() => activeFields(form, values, flow), [form, values, flow]);

  return (
    <div className="form-grid">
      {fields.map((field) => {
        if (isDisplayOnly(field)) return <InfoBlock key={field.name} field={field} language={language} />;
        const error = errorFor(errors, field.name, strings);
        const wide = field.type === 'textarea' || field.type === 'multiselect' || field.type === 'checkbox';
        const id = `field-${field.name}`;

        return (
          <div key={field.name} className={`field${wide ? ' field-wide' : ''}`}>
            {field.type === 'checkbox' ? null : (
              <label htmlFor={id}>
                {localized(field.label, field.labelI18n, language)}
                <span className="hint">
                  {field.required ? `(${strings.required})` : `(${strings.optional})`}
                </span>
              </label>
            )}

            <FieldControl
              field={field}
              lists={lists}
              value={values[field.name] ?? null}
              language={language}
              strings={strings}
              invalid={Boolean(error)}
              onChange={(value) => onChange(field.name, value)}
            />

            {field.helpText ? (
              <div className="help" id={`${id}-help`}>
                {localized(field.helpText, field.helpTextI18n, language)}
              </div>
            ) : null}

            {error ? (
              <div className="error" role="alert">
                <Icon name="alert" size={16} />
                {error}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
